import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getContactsByCustomerId } from '@/lib/demo-data/customer-communications-contacts';
import { mapContactApiToUi } from '@/lib/mappers/customer-detail';
import { useMode } from '@/providers/ModeProvider';
import { createContactDemo, deleteContactDemo, updateContactDemo } from '@/services/demo/contacts-demo.service';
import type { ContactApi, ContactsApiResponse, StubResponse } from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import type { ContactUI } from '@/types/ui/customer-detail';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

interface ContactInput {
  title?: string;
  firstName?: string;
  surname?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  preferredContactMethod?: string;
  jobTitle?: string;
  company?: string;
  notes?: string;
  isPrimary: boolean;
}

interface ContactUpdateInput {
  title?: string;
  firstName?: string;
  surname?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  preferredContactMethod?: string;
  jobTitle?: string;
  company?: string;
  notes?: string;
  isPrimary?: boolean;
}

/**
 * Mode-aware query hook for fetching customer contacts.
 * Returns contacts reactively via useModeAwareQuery, respecting the
 * current screen's Demo/Live toggle.
 */
export function useCustomerContactsList(customerId: string) {
  const { apiGet } = useApi();
  const mockContacts = getContactsByCustomerId(customerId).map(mapContactApiToUi);

  return useModeAwareQuery<ContactUI[], Error>(
    ['customer-contacts', customerId],
    async ({ signal }) => {
      if (!customerId || customerId === '0') return [];
      const response = await apiGet<ContactsApiResponse | StubResponse>(
        `/api/v1/customers/${customerId}/contacts`,
        undefined,
        signal,
      );

      if (isStubResponse(response)) {
        return getContactsByCustomerId(customerId).map(mapContactApiToUi);
      }

      return response.contacts.map(mapContactApiToUi);
    },
    mockContacts,
    {
      enabled: !!customerId && customerId !== '0',
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Mode-aware query hook for fetching contacts for multiple customers.
 * Returns combined contacts from all specified customer IDs.
 */
export function useBulkCustomerContactsList(customerIds: string[]) {
  const { apiGet } = useApi();

  // Get mock contacts for all customers
  const mockContacts = customerIds.flatMap((id) => getContactsByCustomerId(id).map(mapContactApiToUi));

  const sortedCustomerIds = [...customerIds].sort((a, b) => a.localeCompare(b));

  return useModeAwareQuery<ContactUI[], Error>(
    ['bulk-customer-contacts', ...sortedCustomerIds],
    async ({ signal }) => {
      if (!customerIds || customerIds.length === 0) return [];

      // Fetch contacts for each customer in parallel
      const contactPromises = customerIds.map(async (customerId) => {
        if (!customerId || customerId === '0') return [];

        try {
          const response = await apiGet<ContactsApiResponse | StubResponse>(
            `/api/v1/customers/${customerId}/contacts`,
            undefined,
            signal,
          );

          if (isStubResponse(response)) {
            return getContactsByCustomerId(customerId).map(mapContactApiToUi);
          }

          return response.contacts.map(mapContactApiToUi);
        } catch (error) {
          console.error(`Failed to fetch contacts for customer ${customerId}:`, error);
          return [];
        }
      });

      const contactsArrays = await Promise.all(contactPromises);
      return contactsArrays.flat();
    },
    mockContacts,
    {
      enabled: customerIds.length > 0,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook for customer contact mutation operations (create, update, delete).
 * Uses authenticated API calls with proper headers in live mode; in demo mode
 * the mutations apply to the demo contacts store instead of calling the API.
 */
export function useCustomerContacts(customerId: string) {
  const { apiPost, apiPut, apiDelete } = useApi();
  const { isDemo } = useMode();
  const queryClient = useQueryClient();

  // Demo queries capture their mock data per render, so an invalidated refetch
  // resolves with the snapshot taken before the mutation; dropping the queries
  // makes the next fetch read the mutated demo store.
  const refreshContacts = useCallback(() => {
    const queryKeys = [
      ['customer-contacts', customerId],
      ['customer-detail', customerId],
      ['case-contacts', customerId],
      ['create-promise-contacts', customerId],
    ];

    for (const queryKey of queryKeys) {
      if (isDemo) {
        queryClient.removeQueries({ queryKey });
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  }, [queryClient, customerId, isDemo]);

  const { mutateAsync: createContactAsync } = useModeAwareMutation<ContactApi, Error, ContactInput>(
    (contact) => apiPost<ContactApi>(`/api/v1/customers/${customerId}/contacts`, contact),
    (contact) => createContactDemo({ customerId, contact }),
    { onSuccess: refreshContacts },
  );

  const { mutateAsync: updateContactAsync } = useModeAwareMutation<
    void,
    Error,
    { contactId: string; contact: ContactUpdateInput }
  >(
    async ({ contactId, contact }) => {
      await apiPut<unknown>(`/api/v1/customers/${customerId}/contacts/${contactId}`, contact);
    },
    ({ contactId, contact }) => updateContactDemo({ customerId, contactId, contact }),
    { onSuccess: refreshContacts },
  );

  const { mutateAsync: deleteContactAsync } = useModeAwareMutation<void, Error, string>(
    async (contactId) => {
      await apiDelete<void>(`/api/v1/customers/${customerId}/contacts/${contactId}`);
    },
    (contactId) => deleteContactDemo({ customerId, contactId }),
    { onSuccess: refreshContacts },
  );

  const createContact = useCallback((contact: ContactInput) => createContactAsync(contact), [createContactAsync]);

  const updateContact = useCallback(
    (contactId: string, contact: ContactUpdateInput) => updateContactAsync({ contactId, contact }),
    [updateContactAsync],
  );

  const deleteContact = useCallback((contactId: string) => deleteContactAsync(contactId), [deleteContactAsync]);

  const setContactAsPrimary = useCallback(
    (contactId: string) => updateContact(contactId, { isPrimary: true }),
    [updateContact],
  );

  return {
    createContact,
    updateContact,
    deleteContact,
    setContactAsPrimary,
  };
}
